// React Query hooks for BMS API
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { getApiClient } from '../api/client.js';
import { useAuthStore } from '../store/authStore.js';
import { permissionScopeKey } from '../types/permissions.js';
import { buildSiteRoomsTopicFilter, createTrailingDebounce, subscribeShared, useAllGatewayStatuses, } from './useMqtt.js';
import { encodePresenceHeader } from '../utils/virtualDevice.js';
// =============================================================================
// Query Keys
// =============================================================================
export const queryKeys = {
    // Auth
    currentUser: ['auth', 'me'],
    // Users & Roles
    users: (siteCode) => ['users', siteCode ?? 'global'],
    user: (userId, siteCode) => ['users', userId, siteCode ?? 'global'],
    roles: ['roles'],
    userDevices: (userId) => ['users', userId, 'devices'],
    deviceInvitations: (userId) => ['users', userId, 'device-invitations'],
    deviceInvitationClaimStatus: (invitationId, claimReceipt) => ['device-invitation-status', invitationId, claimReceipt],
    // Sites
    sites: ['sites'],
    siteConfig: ['site'],
    // Provisioning
    discoveredGateways: ['provisioning', 'gateways'],
    discoveredGateway: (hwUid) => ['provisioning', 'gateways', hwUid],
    // Gateways (simplified: uses gatewayCode as single identifier)
    gateways: (siteCode) => ['gateways', siteCode],
    gateway: (siteCode, gatewayCode) => ['gateways', siteCode, gatewayCode],
    // Rooms (include site for cache key)
    rooms: (siteCode) => ['rooms', siteCode],
    roomLabels: (siteCode) => ['rooms', siteCode, 'labels'],
    room: (siteCode, roomCode) => ['rooms', siteCode, roomCode],
    // Buildings & Floors
    buildings: (siteCode) => ['buildings', siteCode],
    floors: (siteCode, buildingCode) => ['buildings', siteCode, buildingCode, 'floors'],
    // Gateway-Room associations
    roomGateways: (siteCode, roomCode) => ['rooms', siteCode, roomCode, 'gateways'],
    gatewayRooms: (siteCode, gatewayCode) => ['gateways', siteCode, gatewayCode, 'rooms'],
    gatewayDevices: (siteCode, gatewayCode) => ['gateways', siteCode, gatewayCode, 'devices'],
    deviceConfig: (siteCode, gatewayCode, deviceCode) => ['gateways', siteCode, gatewayCode, 'devices', deviceCode, 'config'],
    // Channels
    channels: (siteCode, roomCode) => ['rooms', siteCode, roomCode, 'channels'],
    channel: (siteCode, roomCode, chCode) => ['rooms', siteCode, roomCode, 'channels', chCode],
    channelHistory: (siteCode, roomCode, chCode) => ['rooms', siteCode, roomCode, 'channels', chCode, 'history'],
    // Templates
    templates: (siteCode) => ['templates', siteCode],
    template: (siteCode, templateCode) => ['templates', siteCode, templateCode],
    // Gateway Templates
    gatewayTemplates: (siteCode) => ['gatewayTemplates', siteCode],
    gatewayTemplate: (siteCode, templateCode) => ['gatewayTemplates', siteCode, templateCode],
    // Scenes
    scenes: (siteCode, roomCode) => ['rooms', siteCode, roomCode, 'scenes'],
    // Room Params
    roomParams: (siteCode, roomCode) => ['rooms', siteCode, roomCode, 'params'],
    hotelThermostat: (siteCode, roomCode) => ['rooms', siteCode, roomCode, 'hotel-thermostat'],
    roomBindings: (siteCode, roomCode) => ['rooms', siteCode, roomCode, 'bindings'],
    roomBindingSuggestions: (siteCode, roomCode) => ['rooms', siteCode, roomCode, 'bindingSuggestions'],
    // System
    version: ['version'],
    health: ['health'],
    diagnostics: ['diagnostics'],
    events: (params) => ['events', params ?? {}],
    eventStats: ['eventStats'],
    commissioningGaps: (siteCode) => ['commissioningGaps', siteCode],
    // Notifications & Alert Rules
    notifications: (siteCode, params) => ['notifications', siteCode, params ?? {}],
    unreadCount: (siteCode) => ['notifications', siteCode, 'unread'],
    alertRules: (siteCode) => ['alertRules', siteCode],
    // Access Control
    locks: (siteCode, filters) => ['locks', siteCode, filters ?? {}],
    lockDetail: (siteCode, lockCode) => ['locks', siteCode, lockCode],
    lockEvents: (siteCode, filters) => ['lockEvents', siteCode, filters ?? {}],
    readers: (siteCode) => ['readers', siteCode],
    readerLocks: (siteCode, readerChannelId) => ['readerLocks', siteCode, readerChannelId],
    lockReaders: (siteCode, lockCode) => ['lockReaders', siteCode, lockCode],
    zones: (siteCode, filters) => ['zones', siteCode, filters ?? {}],
    zone: (siteCode, zoneCode) => ['zones', siteCode, zoneCode],
    zoneChannels: (siteCode, zoneCode) => ['zones', siteCode, zoneCode, 'channels'],
    zoneChannel: (siteCode, zoneCode, chCode) => ['zones', siteCode, zoneCode, 'channels', chCode],
    // Access Cards & Lock Groups
    accessCards: (siteCode, filters) => ['accessCards', siteCode, filters ?? {}],
    accessCardDetail: (siteCode, cardUid) => ['accessCards', siteCode, cardUid],
    // Under `['accessCards', site]` so every card mutation and card event that
    // invalidates the card lists refreshes the counts too.
    guestKeySummary: (siteCode) => ['accessCards', siteCode, { guestKeySummary: true }],
    // Same reason: a desk key write (`useIssueCard`) must refresh the room dialog's key count.
    roomCards: (siteCode, roomCode) => ['accessCards', siteCode, { room: roomCode }],
    cardRoles: (siteCode) => ['cardRoles', siteCode],
    cardRole: (siteCode, roleName) => ['cardRoles', siteCode, roleName],
    lockGroups: (siteCode) => ['lockGroups', siteCode],
    lockGroup: (siteCode, groupCode) => ['lockGroups', siteCode, groupCode],
    // Access schedules — a separate entity from the cron `schedules` keys below.
    accessSchedules: (siteCode) => ['accessSchedules', siteCode],
    accessSchedule: (siteCode, scheduleCode) => ['accessSchedules', siteCode, scheduleCode],
    lockSchedules: (siteCode, lockCode) => ['accessSchedules', 'lock', siteCode, lockCode],
    cardSchedules: (siteCode, cardId) => ['accessSchedules', 'card', siteCode, cardId],
    // Access analytics. Keyed by the range so a widened range refetches rather
    // than reading a narrower one's cache.
    accessAnalyticsSummary: (siteCode, query) => ['accessAnalytics', 'summary', siteCode, query],
    // Visitors and visits. A visitor's CARD lives under the accessCards keys: the
    // credential is an ordinary card and is not keyed separately here.
    visitors: (siteCode, filters) => ['visitors', siteCode, filters ?? {}],
    visitor: (siteCode, visitorId) => ['visitors', siteCode, visitorId],
    visits: (siteCode, filters) => ['visits', siteCode, filters ?? {}],
    visit: (siteCode, visitId) => ['visits', siteCode, visitId],
    // SAM Enrollment
    samEnrollments: (siteCode) => ['samEnrollments', siteCode],
    samMaterial: (siteCode) => ['samMaterial', siteCode],
    signingKeys: (siteCode) => ['signingKeys', siteCode],
    // GRMS
    occupancySummary: (siteCode) => ['grms', 'summary', siteCode],
    todayStays: (siteCode) => ['grms', 'stays', 'today', siteCode],
    housekeepingSummary: (siteCode) => ['grms', 'housekeeping', 'summary', siteCode],
    grmsAnalytics: (siteCode, days) => ['grms', 'analytics', siteCode, days],
    pmsConnection: (siteCode) => ['grms', 'pms', 'connection', siteCode],
    roomEvents: (siteCode, roomCode) => ['grms', 'roomEvents', siteCode, roomCode],
    roomHistory: (siteCode, roomCode) => ['grms', 'roomHistory', siteCode, roomCode],
    // Alarms
    activeAlarms: (siteCode) => ['alarms', siteCode],
    alarmHistory: (siteCode, params) => ['alarms', siteCode, 'history', params ?? {}],
    roomAlarms: (siteCode, roomCode) => ['alarms', siteCode, roomCode],
    // Schedules
    schedules: (siteCode) => ['schedules', siteCode],
    schedule: (siteCode, id) => ['schedules', siteCode, id],
    // Floor plans
    cardTemplates: (siteCode) => ['cardTemplates', siteCode],
    cardTemplate: (siteCode, code) => ['cardTemplates', siteCode, code],
    // Room Layouts
    roomLayouts: (siteCode) => ['roomLayouts', siteCode],
    roomLayout: (siteCode, code) => ['roomLayouts', siteCode, code],
    // Firmware / OTA
    firmwareList: (product) => ['firmware', product ?? 'all'],
    firmware: (id) => ['firmware', id],
    deployments: (siteCode, filters) => ['deployments', siteCode, filters ?? {}],
    versionMatrix: (siteCode) => ['firmware', 'matrix', siteCode],
    // Virtual Devices (room emulator control plane)
    virtualRoutes: ['virtual', 'routes'],
    virtualHealth: ['virtual', 'health'],
    virtualProfiles: ['virtual', 'profiles'],
    virtualScenarios: ['virtual', 'scenarios'],
    virtualInstances: ['virtual', 'instances'],
    virtualInstance: (hwUid) => ['virtual', 'instances', hwUid],
    virtualFleet: ['virtual', 'fleet'],
    virtualFleetRoles: ['virtual', 'fleet', 'roles'],
};
// =============================================================================
// Helper: Get API client with site code from store
// =============================================================================
function getConfiguredApiClient() {
    const client = getApiClient();
    // Set up the site code getter to read from the auth store
    client.setSiteCodeGetter(() => useAuthStore.getState().currentSiteCode);
    client.setAccessTokenGetter(() => useAuthStore.getState().token);
    client.setAuthFailureHandler(() => useAuthStore.getState().logout());
    client.setSessionHandler(({ token, broker }) => useAuthStore.getState().setSession(token, broker));
    return client;
}
// =============================================================================
// Auth Hooks
// =============================================================================
/**
 * The raw Current-user permission projection read for one scope.
 *
 * **Protected controls must use `usePermissions` instead.** This hook performs
 * the read and nothing else: it does not clear the previous scope before
 * fetching, does not revalidate on the visible-control bound, does not
 * invalidate on 403, and does not fail closed when a response arrives for
 * another identity or site. Those are exactly the rules that make a permission
 * answer safe to gate a control on, and they live in `usePermissions`.
 *
 * Use this one for displaying who is signed in, and for scopes you are merely
 * reporting on rather than authorizing against.
 */
export function useCurrentUser(siteCode, options) {
    const identityId = useAuthStore((state) => state.user?.id ?? null);
    return useQuery({
        queryKey: permissionScopeKey(identityId, siteCode ?? null),
        queryFn: () => getConfiguredApiClient().getCurrentUser(siteCode),
        retry: false,
        ...options,
    });
}
export function useLogin() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ username, password }) => getConfiguredApiClient().login(username, password),
        onSuccess: () => {
            // The login payload keeps its existing shape and is *not* written into
            // the permission cache. It carries roles, not resolved grants, and
            // seeding it here would let a protected control read a login response as
            // an authorization answer. Login is followed by an explicit projection
            // read — see `usePermissions` — not overloaded into one.
            queryClient.removeQueries({ queryKey: queryKeys.currentUser });
        },
    });
}
export function useSelectSite() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ siteCode }) => getConfiguredApiClient().selectSite(siteCode),
        onSuccess: () => {
            // Refresh sites and other queries after site selection
            queryClient.invalidateQueries({ queryKey: queryKeys.sites });
            // The scope changed, so every cached projection is about the previous
            // one. Dropped rather than refetched here: `usePermissions` reads the new
            // scope, and a control must not be offered on the old site's answer in
            // the meantime.
            queryClient.removeQueries({ queryKey: queryKeys.currentUser });
        },
    });
}
export function useLogout() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => getConfiguredApiClient().logout(),
        onSuccess: () => {
            queryClient.clear();
        },
    });
}
// =============================================================================
// Users & Roles Hooks (RBAC)
// =============================================================================
export function useUsers(siteCode, options) {
    return useQuery({
        queryKey: queryKeys.users(siteCode),
        queryFn: () => getConfiguredApiClient().getUsers(siteCode),
        staleTime: 30 * 1000,
        ...options,
    });
}
export function useUser(userId, siteCode, options) {
    return useQuery({
        queryKey: queryKeys.user(userId, siteCode),
        queryFn: () => getConfiguredApiClient().getUser(userId, siteCode),
        enabled: !!userId,
        ...options,
    });
}
export function useCreateUser() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload) => getConfiguredApiClient().createUser(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
        },
    });
}
export function useUpdateUser() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ userId, payload }) => getConfiguredApiClient().updateUser(userId, payload),
        onSuccess: (_, { userId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.user(userId) });
            queryClient.invalidateQueries({ queryKey: ['users'] });
        },
    });
}
export function useDeleteUser() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (userId) => getConfiguredApiClient().deleteUser(userId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['users'] });
        },
    });
}
export function useUpdateUserRoles() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ userId, payload }) => getConfiguredApiClient().updateUserRoles(userId, payload),
        onSuccess: (_, { userId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.user(userId) });
            queryClient.invalidateQueries({ queryKey: ['users'] });
        },
    });
}
export function useUserDevices(userId) {
    return useQuery({
        queryKey: queryKeys.userDevices(userId),
        queryFn: () => getConfiguredApiClient().getUserDevices(userId),
        enabled: !!userId,
    });
}
export function useRevokeUserDevice() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ userId, credentialId }) => getConfiguredApiClient().revokeUserDevice(userId, credentialId),
        onSuccess: (_, { userId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.userDevices(userId) });
        },
    });
}
export function useDeviceInvitations(userId, options) {
    return useQuery({
        queryKey: queryKeys.deviceInvitations(userId),
        queryFn: () => getConfiguredApiClient().getDeviceInvitations(userId),
        enabled: !!userId,
        ...options,
    });
}
export function useCreateDeviceInvitation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ userId, payload }) => getConfiguredApiClient().createDeviceInvitation(userId, payload),
        onSuccess: (_, { userId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.deviceInvitations(userId) });
        },
    });
}
export function useCancelDeviceInvitation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ userId, invitationId }) => getConfiguredApiClient().cancelDeviceInvitation(userId, invitationId),
        onSuccess: (_, { userId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.deviceInvitations(userId) });
        },
    });
}
export function useConfirmDeviceInvitation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ userId, invitationId, payload }) => getConfiguredApiClient().confirmDeviceInvitation(userId, invitationId, payload),
        onSuccess: (_, { userId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.deviceInvitations(userId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.userDevices(userId) });
        },
    });
}
/**
 * Bounded receipt-only polling for the phone claim flow. `gcTime: 0` makes a
 * receipt leave the React Query cache when the pending UI unmounts; callers must
 * stop rendering this hook as soon as a terminal state is observed.
 */
export function useDeviceInvitationClaimStatus(invitationId, claimReceipt, expiresAt) {
    return useQuery({
        queryKey: queryKeys.deviceInvitationClaimStatus(invitationId, claimReceipt ?? ''),
        queryFn: () => getConfiguredApiClient().getDeviceInvitationClaimStatus(invitationId, claimReceipt),
        enabled: () => !!invitationId && !!claimReceipt && !!expiresAt && Date.parse(expiresAt) > Date.now(),
        refetchInterval: (query) => {
            const state = query.state.data?.state;
            return Date.parse(expiresAt ?? '') > Date.now() && (state === undefined || state === 'claimed')
                ? 2_000
                : false;
        },
        gcTime: 0,
        retry: false,
    });
}
export function useRoles(options) {
    return useQuery({
        queryKey: queryKeys.roles,
        queryFn: () => getConfiguredApiClient().getRoles(),
        staleTime: 10 * 60 * 1000,
        ...options,
    });
}
// =============================================================================
// Site Hooks
// =============================================================================
export function useSites(options) {
    return useQuery({
        queryKey: queryKeys.sites,
        queryFn: () => getConfiguredApiClient().getSites(),
        staleTime: 60 * 1000, // 1 minute
        ...options,
    });
}
export function useCreateSite() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (site) => getConfiguredApiClient().createSite(site),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.sites });
        },
    });
}
// =============================================================================
// Site Config Hooks
// =============================================================================
export function useSiteConfig(options) {
    return useQuery({
        queryKey: queryKeys.siteConfig,
        queryFn: () => getConfiguredApiClient().getSiteConfig(),
        staleTime: 10 * 60 * 1000, // 10 minutes
        ...options,
    });
}
/**
 * The narrow occupancy-automation write (`site:write_occupancy_automation`).
 *
 * The response is the resulting `occupancy_automation` object and **not** the
 * site config, so the site-config cache is invalidated and re-read rather than
 * written from it — a caller holding only this permission cannot see the rest
 * of `settings`, and seeding the cache from a partial answer would blank
 * everything else the page reads.
 */
export function useUpdateOccupancyAutomation() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (patch) => getConfiguredApiClient().updateOccupancyAutomation(patch),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.siteConfig });
        },
    });
}
export function useBranding(options) {
    return useQuery({
        queryKey: ['branding'],
        queryFn: () => getConfiguredApiClient().getBranding(),
        staleTime: 10 * 60 * 1000, // 10 minutes - rarely changes
        ...options,
    });
}
export function useUpdateSiteConfig() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (config) => getConfiguredApiClient().updateSiteConfig(config),
        onSuccess: (data) => {
            queryClient.setQueryData(queryKeys.siteConfig, data);
        },
    });
}
export function useUploadBrandingLogo() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (file) => getConfiguredApiClient().uploadBrandingLogo(file),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.siteConfig });
        },
    });
}
// =============================================================================
// Buildings & Floors Hooks (Site-scoped, full profile only)
// =============================================================================
export function useBuildings(options) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.buildings(siteCode || ''),
        queryFn: () => getConfiguredApiClient().getBuildings(),
        enabled: !!siteCode,
        ...options,
    });
}
export function useCreateBuilding() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: (building) => getConfiguredApiClient().createBuilding(building),
        onSuccess: () => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.buildings(siteCode) });
            }
        },
    });
}
export function useUpdateBuilding() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ buildingCode, building }) => getConfiguredApiClient().updateBuilding(buildingCode, building),
        onSuccess: () => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.buildings(siteCode) });
            }
        },
    });
}
export function useDeleteBuilding() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: (buildingCode) => getConfiguredApiClient().deleteBuilding(buildingCode),
        onSuccess: () => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.buildings(siteCode) });
                // Rooms may have lost building/floor references
                queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode) });
            }
        },
    });
}
export function useFloors(buildingCode, options) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.floors(siteCode || '', buildingCode),
        queryFn: () => getConfiguredApiClient().getFloors(buildingCode),
        enabled: !!siteCode && !!buildingCode,
        ...options,
    });
}
export function useCreateFloor() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ buildingCode, floor }) => getConfiguredApiClient().createFloor(buildingCode, floor),
        onSuccess: (_, { buildingCode }) => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.floors(siteCode, buildingCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.buildings(siteCode) });
            }
        },
    });
}
export function useUpdateFloor() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ buildingCode, floorCode, floor }) => getConfiguredApiClient().updateFloor(buildingCode, floorCode, floor),
        onSuccess: (_, { buildingCode }) => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.floors(siteCode, buildingCode) });
            }
        },
    });
}
export function useDeleteFloor() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ buildingCode, floorCode }) => getConfiguredApiClient().deleteFloor(buildingCode, floorCode),
        onSuccess: (_, { buildingCode }) => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.floors(siteCode, buildingCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.buildings(siteCode) });
                // Rooms may have lost floor references
                queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode) });
            }
        },
    });
}
// =============================================================================
// Provisioning Hooks
// =============================================================================
export function useDiscoveredGateways(options) {
    return useQuery({
        queryKey: queryKeys.discoveredGateways,
        queryFn: () => getConfiguredApiClient().getDiscoveredGateways(),
        // No polling - discovered gateways detected via MQTT status messages
        ...options,
    });
}
export function useDiscoveredGateway(hwUid, options) {
    return useQuery({
        queryKey: queryKeys.discoveredGateway(hwUid),
        queryFn: () => getConfiguredApiClient().getDiscoveredGateway(hwUid),
        enabled: !!hwUid,
        ...options,
    });
}
export function useClaimGateway() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ hwUid, params }) => getConfiguredApiClient().claimGateway(hwUid, params),
        onSuccess: () => {
            // Invalidate both provisioning and gateways lists
            queryClient.invalidateQueries({ queryKey: queryKeys.discoveredGateways });
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.gateways(siteCode) });
            }
        },
    });
}
// =============================================================================
// Orphaned Gateways Hooks
// =============================================================================
export function useOrphanedGateways(options) {
    return useQuery({
        queryKey: ['provisioning', 'orphaned'],
        queryFn: () => getConfiguredApiClient().getOrphanedGateways(),
        // No polling - orphans detected via MQTT status messages
        ...options,
    });
}
// No useForceUnclaimOrphan hook: its route and client method are removed under BMS-spec
// docs/edge/rest-api.md §Orphan recovery controls. Recovery goes through useReconcileOrphan
// (execute:false = Inspect recovery, execute:true = Retry reclaim) or useDetachOrphan.
export function useReclaimOrphan() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (hwUid) => getConfiguredApiClient().reclaimOrphan(hwUid),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['provisioning', 'orphaned'] });
            queryClient.invalidateQueries({ queryKey: queryKeys.discoveredGateways });
        },
    });
}
export function useReconcileOrphan() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ hwUid, execute }) => getConfiguredApiClient().reconcileOrphan(hwUid, { execute }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['provisioning', 'orphaned'] });
            queryClient.invalidateQueries({ queryKey: queryKeys.discoveredGateways });
        },
    });
}
export function useDetachOrphan() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (hwUid) => getConfiguredApiClient().detachOrphan(hwUid),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['provisioning', 'orphaned'] });
            queryClient.invalidateQueries({ queryKey: queryKeys.discoveredGateways });
        },
    });
}
// =============================================================================
// Gateway Hooks (Site-scoped)
// =============================================================================
export function useGateways(options) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.gateways(siteCode || ''),
        queryFn: () => getConfiguredApiClient().getGateways(),
        refetchInterval: 60000, // Reduced to 60s - MQTT provides real-time updates
        enabled: !!siteCode,
        ...options,
    });
}
/**
 * Real-time gateways hook - combines REST API with live MQTT status updates
 *
 * This hook merges:
 * - Initial gateway list from REST API (60s polling for new gateways)
 * - Live status/identify updates from MQTT (real-time, <1s latency)
 *
 * Requires a complete session and its broker credential.
 */
export function useGatewaysLive(options) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    const restQuery = useGateways(options);
    const liveStatuses = useAllGatewayStatuses(siteCode);
    // Merge live MQTT data into REST gateway list
    // Convert Map to array for stable dependency (Map reference doesn't change, but contents do)
    const liveStatusArray = useMemo(() => Array.from(liveStatuses.entries()), [liveStatuses]);
    const gatewaysWithLiveData = useMemo(() => {
        if (!restQuery.data)
            return [];
        // Rebuild Map from array for lookup
        const statusMap = new Map(liveStatusArray);
        return restQuery.data.map((gateway) => {
            const liveStatus = statusMap.get(gateway.gatewayCode);
            if (liveStatus) {
                return {
                    ...gateway,
                    status: liveStatus.status,
                    identify: liveStatus.identify,
                    lastStatusAt: liveStatus.lastSeen,
                    firmwareVersion: liveStatus.firmwareVersion || gateway.firmwareVersion,
                };
            }
            return gateway;
        });
    }, [restQuery.data, liveStatusArray]);
    return {
        ...restQuery,
        data: gatewaysWithLiveData,
    };
}
export function useGateway(gatewayCode, options) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.gateway(siteCode || '', gatewayCode),
        queryFn: () => getConfiguredApiClient().getGateway(gatewayCode),
        enabled: !!siteCode && !!gatewayCode,
        ...options,
    });
}
export function useMapChannels() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ gatewayCode, mapping }) => getConfiguredApiClient().mapChannels(gatewayCode, mapping),
        onSuccess: (_, { gatewayCode }) => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.gateway(siteCode, gatewayCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode) });
            }
        },
    });
}
export function useUpdateGateway() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ gatewayCode, ...updates }) => getConfiguredApiClient().updateGateway(gatewayCode, updates),
        onSuccess: (_data, variables) => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.gateways(siteCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.gateway(siteCode, variables.gatewayCode) });
            }
        },
    });
}
export function useUpdateChannelName() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ gatewayCode, channelId, channelName }) => getConfiguredApiClient().updateChannelName(gatewayCode, channelId, channelName),
        onSuccess: (_data, variables) => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.gateway(siteCode, variables.gatewayCode) });
                // Channel name is also shown on room pages — invalidate all rooms
                queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode) });
            }
        },
    });
}
export function useUpdateChannel() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ gatewayCode, channelId, ...params }) => getConfiguredApiClient().updateChannel(gatewayCode, channelId, params),
        onSuccess: (_data, variables) => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.gateway(siteCode, variables.gatewayCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode) });
            }
        },
    });
}
export function useUnclaimGateway() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: (gatewayCode) => getConfiguredApiClient().unclaimGateway(gatewayCode),
        onSuccess: () => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.gateways(siteCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.discoveredGateways });
            }
        },
    });
}
export function useReplaceGateway() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ gatewayCode, hwUid }) => getConfiguredApiClient().replaceGateway(gatewayCode, hwUid),
        onSuccess: (_data, variables) => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.gateway(siteCode, variables.gatewayCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.gateways(siteCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.discoveredGateways });
            }
        },
    });
}
export function usePushCryptoToGateway() {
    return useMutation({
        mutationFn: ({ gatewayCode }) => getConfiguredApiClient().pushCryptoToGateway(gatewayCode),
    });
}
export function usePushCryptoToAllGateways() {
    return useMutation({
        mutationFn: () => getConfiguredApiClient().pushCryptoToAllGateways(),
    });
}
export function useGatewayDevices(gatewayCode, options) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.gatewayDevices(siteCode || '', gatewayCode),
        queryFn: () => getConfiguredApiClient().getGatewayDevices(gatewayCode),
        enabled: !!siteCode && !!gatewayCode,
        ...options,
    });
}
export function useUpdateGatewayDevice() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ gatewayCode, deviceCode, displayName }) => getConfiguredApiClient().updateGatewayDevice(gatewayCode, deviceCode, displayName),
        onSuccess: (_data, variables) => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.gatewayDevices(siteCode, variables.gatewayCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.gateway(siteCode, variables.gatewayCode) });
            }
        },
    });
}
export function useRemoveGatewayDevice() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ gatewayCode, deviceCode }) => getConfiguredApiClient().removeGatewayDevice(gatewayCode, deviceCode),
        onSuccess: (_data, variables) => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.gatewayDevices(siteCode, variables.gatewayCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.gateway(siteCode, variables.gatewayCode) });
                queryClient.invalidateQueries({ queryKey: ['rooms'] });
            }
        },
    });
}
/** Start an Edge-initiated reset of a peripheral; the job's result arrives on the device's config read. */
export function useResetGatewayDevice() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ gatewayCode, deviceCode, full, recover }) => getConfiguredApiClient().resetGatewayDevice(gatewayCode, deviceCode, { ...(full !== undefined ? { full } : {}), ...(recover !== undefined ? { recover } : {}) }),
        onSuccess: (_data, variables) => {
            if (siteCode)
                queryClient.invalidateQueries({ queryKey: queryKeys.deviceConfig(siteCode, variables.gatewayCode, variables.deviceCode) });
        },
    });
}
/** Settle a pending peripheral config job with the outcome the operator states (BMS-spec config-relay.md "Operator settlement"). */
export function useSettleConfigJob() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ gatewayCode, deviceCode, requestId, outcome, note }) => getConfiguredApiClient().settleConfigJob(gatewayCode, deviceCode, requestId, { outcome, note }),
        onSuccess: (_data, variables) => {
            if (siteCode)
                queryClient.invalidateQueries({ queryKey: queryKeys.deviceConfig(siteCode, variables.gatewayCode, variables.deviceCode) });
        },
    });
}
export function useDeviceConfig(gatewayCode, deviceCode, options) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.deviceConfig(siteCode || '', gatewayCode, deviceCode),
        queryFn: () => getConfiguredApiClient().getDeviceConfig(gatewayCode, deviceCode),
        enabled: !!siteCode && !!gatewayCode && !!deviceCode,
        ...options,
    });
}
/** Thrown when the device answered a config command with `result: rejected`. */
export class DeviceConfigRejectedError extends Error {
    constructor(message) {
        super(message);
        this.name = 'DeviceConfigRejectedError';
    }
}
/**
 * PATCH a device config, then read GET config until the device's correlated answer for this
 * request arrives (BMS-spec rest-api.md: PATCH only accepts delivery). A refusal throws with
 * the device's message; no answer within the wait resolves `confirmed: false`.
 */
export async function sendDeviceConfigAndConfirm(gatewayCode, deviceCode, params, saveAsDefaults, { waitMs = 8000, pollMs = 700 } = {}) {
    const api = getConfiguredApiClient();
    const { requestId } = await api.updateDeviceConfig(gatewayCode, deviceCode, params, saveAsDefaults);
    if (!requestId)
        return { confirmed: false };
    const deadline = Date.now() + waitMs;
    while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, pollMs));
        const state = await api.getDeviceConfig(gatewayCode, deviceCode);
        if (state.requestId !== requestId)
            continue;
        if (state.result === 'rejected')
            throw new DeviceConfigRejectedError(state.message ?? 'The device refused the configuration.');
        if (state.result === 'applied')
            return { confirmed: true, requestId };
    }
    return { confirmed: false, requestId };
}
export function useUpdateDeviceConfig() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ gatewayCode, deviceCode, params, saveAsDefaults }) => sendDeviceConfigAndConfirm(gatewayCode, deviceCode, params, saveAsDefaults),
        onSuccess: (_data, variables) => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.deviceConfig(siteCode, variables.gatewayCode, variables.deviceCode) });
            }
        },
    });
}
// =============================================================================
// Room Hooks (Site-scoped)
// =============================================================================
/**
 * The site's room list. `params.fields = 'board'` requests the room-board
 * projection (no `metadata`) under the same key:
 * it is a subset of the full row that no other User-UI reader of the list on a
 * GRMS site needs, and the `/state` overlay patches that one key.
 */
export function useRooms(options, params) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    const fields = params?.fields;
    return useQuery({
        queryKey: queryKeys.rooms(siteCode || ''),
        queryFn: () => getConfiguredApiClient().getRooms(fields ? { fields } : undefined),
        enabled: !!siteCode,
        ...options,
    });
}
export function useRoomLabels() {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.roomLabels(siteCode || ''),
        queryFn: () => getConfiguredApiClient().getRoomLabels(),
        enabled: !!siteCode,
    });
}
/**
 * One room's detail. A caller's `refetchInterval` and `enabled` apply on top
 * of the site/room guard; a poll pauses while the document is hidden unless
 * the caller sets `refetchIntervalInBackground` (audit RT-10).
 */
export function useRoom(roomCode, options) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.room(siteCode || '', roomCode),
        queryFn: () => getConfiguredApiClient().getRoom(roomCode),
        refetchIntervalInBackground: false,
        ...options,
        enabled: !!siteCode && !!roomCode && options?.enabled !== false,
    });
}
export function useCreateRoom() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: (room) => getConfiguredApiClient().createRoom(room),
        onSuccess: () => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode) });
            }
        },
    });
}
export function useUpdateRoom() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ roomCode, room }) => getConfiguredApiClient().updateRoom(roomCode, room),
        onSuccess: (_, { roomCode }) => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.room(siteCode, roomCode) });
            }
        },
    });
}
export function useDeleteRoom() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: (roomCode) => getConfiguredApiClient().deleteRoom(roomCode),
        onSuccess: () => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode) });
            }
        },
    });
}
// =============================================================================
// Gateway-Room Association Hooks (Site-scoped)
// =============================================================================
export function useRoomGateways(roomCode, options) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.roomGateways(siteCode || '', roomCode),
        queryFn: () => getConfiguredApiClient().getRoomGateways(roomCode),
        enabled: !!siteCode && !!roomCode,
        ...options,
    });
}
export function useAssociateGateway() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ roomCode, gatewayCode }) => getConfiguredApiClient().associateGateway(roomCode, gatewayCode),
        onSuccess: (_, { roomCode, gatewayCode }) => {
            if (siteCode) {
                // Invalidate room's gateways list
                queryClient.invalidateQueries({ queryKey: queryKeys.roomGateways(siteCode, roomCode) });
                // Invalidate gateway's rooms list
                queryClient.invalidateQueries({ queryKey: queryKeys.gatewayRooms(siteCode, gatewayCode) });
                // Invalidate room detail (may affect channel filtering)
                queryClient.invalidateQueries({ queryKey: queryKeys.room(siteCode, roomCode) });
            }
        },
        onError: (err, { roomCode, gatewayCode }) => {
            // On 409 (already associated), invalidate cache so UI shows current state
            const status = err?.response?.status;
            if (status === 409 && siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.roomGateways(siteCode, roomCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.gatewayRooms(siteCode, gatewayCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.room(siteCode, roomCode) });
            }
        },
    });
}
export function useDissociateGateway() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ roomCode, gatewayCode }) => getConfiguredApiClient().dissociateGateway(roomCode, gatewayCode),
        onSuccess: (_, { roomCode, gatewayCode }) => {
            if (siteCode) {
                // Invalidate room's gateways list
                queryClient.invalidateQueries({ queryKey: queryKeys.roomGateways(siteCode, roomCode) });
                // Invalidate gateway's rooms list
                queryClient.invalidateQueries({ queryKey: queryKeys.gatewayRooms(siteCode, gatewayCode) });
                // Invalidate room detail
                queryClient.invalidateQueries({ queryKey: queryKeys.room(siteCode, roomCode) });
            }
        },
    });
}
export function useGatewayRooms(gatewayCode, options) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.gatewayRooms(siteCode || '', gatewayCode),
        queryFn: () => getConfiguredApiClient().getGatewayRooms(gatewayCode),
        enabled: !!siteCode && !!gatewayCode,
        ...options,
    });
}
// =============================================================================
// Channel Hooks (Site-scoped)
// =============================================================================
export function useChannels(roomCode, options) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.channels(siteCode || '', roomCode),
        queryFn: () => getConfiguredApiClient().getChannels(roomCode),
        enabled: !!siteCode && !!roomCode,
        ...options,
    });
}
export function useChannel(roomCode, chCode, options) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.channel(siteCode || '', roomCode, chCode),
        queryFn: () => getConfiguredApiClient().getChannel(roomCode, chCode),
        enabled: !!siteCode && !!roomCode && !!chCode,
        ...options,
    });
}
export function useSendCommand() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ roomCode, chCode, command }) => getConfiguredApiClient().sendCommand(roomCode, chCode, command),
        onSuccess: (_, { roomCode, chCode }) => {
            // Invalidate channel to refresh value
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.channel(siteCode, roomCode, chCode) });
            }
        },
    });
}
export function useMapRoomChannels() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ roomCode, ...request }) => getConfiguredApiClient().mapRoomChannels(roomCode, request),
        onSuccess: (_, { roomCode }) => {
            // Invalidate room detail to refresh channels
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.room(siteCode, roomCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.channels(siteCode, roomCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.roomGateways(siteCode, roomCode) });
                // Also invalidate gateways so mapping column refreshes on gateway detail page
                queryClient.invalidateQueries({ queryKey: queryKeys.gateways(siteCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode) });
            }
        },
    });
}
export function useUnmapRoomChannel() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ roomCode, chCode }) => getConfiguredApiClient().unmapRoomChannel(roomCode, chCode),
        onSuccess: (_, { roomCode }) => {
            // Invalidate room detail to refresh channels
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.room(siteCode, roomCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.channels(siteCode, roomCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode) });
            }
        },
    });
}
export function useRemoveRoomDeviceChannels() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ roomCode, deviceCode }) => getConfiguredApiClient().removeRoomDeviceChannels(roomCode, deviceCode),
        onSuccess: (_, { roomCode }) => {
            // Invalidate room detail and related queries
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.room(siteCode, roomCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.channels(siteCode, roomCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.roomGateways(siteCode, roomCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode) });
            }
        },
    });
}
export function useChannelHistory(roomCode, chCode, params, options) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: [...queryKeys.channelHistory(siteCode || '', roomCode, chCode), params],
        queryFn: () => getConfiguredApiClient().getChannelHistory(roomCode, chCode, params),
        enabled: !!siteCode && !!roomCode && !!chCode,
        ...options,
    });
}
// =============================================================================
// System Hooks
// =============================================================================
export function useVersion(options) {
    return useQuery({
        queryKey: queryKeys.version,
        queryFn: () => getConfiguredApiClient().getVersion(),
        staleTime: Infinity, // Version doesn't change during session
        ...options,
    });
}
export function useHealth(options) {
    return useQuery({
        queryKey: queryKeys.health,
        queryFn: () => getConfiguredApiClient().getHealth(),
        refetchInterval: 60000, // Check health every minute
        ...options,
    });
}
export function useDiagnostics(options) {
    return useQuery({
        queryKey: queryKeys.diagnostics,
        queryFn: () => getConfiguredApiClient().getDiagnostics(),
        refetchInterval: 10_000, // Auto-refresh every 10 seconds
        ...options,
    });
}
export function useTriggerStaleCheck() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => getConfiguredApiClient().triggerStaleCheck(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.diagnostics });
            queryClient.invalidateQueries({ queryKey: queryKeys.health });
        },
    });
}
export function useFlushCache() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => getConfiguredApiClient().flushCache(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.diagnostics });
        },
    });
}
export function useExportConfig() {
    return useMutation({
        mutationFn: () => getConfiguredApiClient().exportConfig(),
    });
}
export function useExportRoomsCsv() {
    return useMutation({
        mutationFn: (site) => getConfiguredApiClient().exportRoomsCsv(site),
    });
}
export function useExportChannelsCsv() {
    return useMutation({
        mutationFn: (site) => getConfiguredApiClient().exportChannelsCsv(site),
    });
}
export function useExportGatewaysCsv() {
    return useMutation({
        mutationFn: (site) => getConfiguredApiClient().exportGatewaysCsv(site),
    });
}
export function useExportSiteConfig() {
    return useMutation({
        mutationFn: (site) => getConfiguredApiClient().exportSiteConfig(site),
    });
}
// =============================================================================
// Event / Audit Log Hooks
// =============================================================================
export function useEvents(params, options) {
    return useQuery({
        queryKey: queryKeys.events(params),
        queryFn: () => getConfiguredApiClient().getEvents(params),
        refetchInterval: 10_000, // 10s auto-refresh
        ...options,
    });
}
export function useRoomScopedEvents(roomCode, params, options) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    const scopedParams = { ...params, roomCode };
    return useQuery({
        queryKey: ['events', 'room', siteCode ?? '', roomCode, params ?? {}],
        queryFn: () => getConfiguredApiClient().getEvents(scopedParams),
        refetchInterval: 10_000,
        enabled: !!siteCode && !!roomCode && (options?.enabled ?? true),
        ...options,
    });
}
export function useEventStats(options) {
    return useQuery({
        queryKey: queryKeys.eventStats,
        queryFn: () => getConfiguredApiClient().getEventStats(),
        refetchInterval: 30_000, // 30s auto-refresh
        ...options,
    });
}
export function usePurgeEvents() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (params) => getConfiguredApiClient().purgeEvents(params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.eventStats });
            queryClient.invalidateQueries({ queryKey: ['events'] });
        },
    });
}
// =============================================================================
// Commissioning gaps
// =============================================================================
export function useCommissioningGaps(options) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.commissioningGaps(siteCode ?? ''),
        queryFn: () => getConfiguredApiClient().getCommissioningGaps(),
        enabled: !!siteCode && (options?.enabled ?? true),
        refetchInterval: 15_000,
        ...options,
    });
}
// =============================================================================
// Template Hooks (Site-scoped)
// =============================================================================
export function useRoomTemplates(options) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.templates(siteCode || ''),
        queryFn: () => getConfiguredApiClient().getRoomTemplates(),
        enabled: !!siteCode,
        ...options,
    });
}
export function useRoomTemplate(templateCode, options) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.template(siteCode || '', templateCode),
        queryFn: () => getConfiguredApiClient().getRoomTemplate(templateCode),
        enabled: !!siteCode && !!templateCode,
        ...options,
    });
}
export function useCreateRoomTemplate() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: (params) => getConfiguredApiClient().createRoomTemplate(params),
        onSuccess: () => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.templates(siteCode) });
            }
        },
    });
}
export function useUpdateRoomTemplate() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ templateCode, params }) => getConfiguredApiClient().updateRoomTemplate(templateCode, params),
        onSuccess: (_, { templateCode }) => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.templates(siteCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.template(siteCode, templateCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode) });
            }
        },
    });
}
export function useDeleteRoomTemplate() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ templateCode, expectedRevision }) => getConfiguredApiClient().deleteRoomTemplate(templateCode, expectedRevision),
        onSuccess: () => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.templates(siteCode) });
            }
        },
    });
}
export function useApplyRoomTemplate() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ roomCode, params }) => getConfiguredApiClient().applyRoomTemplate(roomCode, params),
        onSuccess: (_, { roomCode }) => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.room(siteCode, roomCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode) });
                queryClient.invalidateQueries({ queryKey: ['roomConfigurator', siteCode] });
                queryClient.invalidateQueries({ queryKey: ['functionBindingSuggestions', siteCode] });
            }
        },
    });
}
export function useDetachRoomTemplate() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ roomCode, expectedVersion }) => getConfiguredApiClient().detachRoomTemplate(roomCode, expectedVersion),
        onSuccess: (_, { roomCode }) => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.room(siteCode, roomCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode) });
                queryClient.invalidateQueries({ queryKey: ['roomConfigurator', siteCode] });
                queryClient.invalidateQueries({ queryKey: ['functionBindingSuggestions', siteCode] });
            }
        },
    });
}
export function useCreateRoomTemplateFromRoom() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ roomCode, params }) => getConfiguredApiClient().createRoomTemplateFromRoom(roomCode, params),
        onSuccess: () => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.templates(siteCode) });
            }
        },
    });
}
export function useReapplyRoomTemplate() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ templateCode, ...body }) => getConfiguredApiClient().reapplyRoomTemplate(templateCode, body),
        onSuccess: () => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode) });
                queryClient.invalidateQueries({ queryKey: ['roomConfigurator', siteCode] });
                queryClient.invalidateQueries({ queryKey: ['functionBindingSuggestions', siteCode] });
            }
        },
    });
}
// =============================================================================
// Gateway Template Hooks (Site-scoped)
// =============================================================================
export function useGatewayTemplates(options) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.gatewayTemplates(siteCode || ''),
        queryFn: () => getConfiguredApiClient().getGatewayTemplates(),
        enabled: !!siteCode,
        ...options,
    });
}
export function useGatewayTemplate(templateCode, options) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.gatewayTemplate(siteCode || '', templateCode),
        queryFn: () => getConfiguredApiClient().getGatewayTemplate(templateCode),
        enabled: !!siteCode && !!templateCode,
        ...options,
    });
}
export function useCreateGatewayTemplate() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: (params) => getConfiguredApiClient().createGatewayTemplate(params),
        onSuccess: () => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.gatewayTemplates(siteCode) });
            }
        },
    });
}
export function useUpdateGatewayTemplate() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ templateCode, params }) => getConfiguredApiClient().updateGatewayTemplate(templateCode, params),
        onSuccess: (_, { templateCode }) => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.gatewayTemplates(siteCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.gatewayTemplate(siteCode, templateCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.gateways(siteCode) });
            }
        },
    });
}
export function useDeleteGatewayTemplate() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: (templateCode) => getConfiguredApiClient().deleteGatewayTemplate(templateCode),
        onSuccess: () => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.gatewayTemplates(siteCode) });
            }
        },
    });
}
export function useApplyGatewayTemplate() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ gatewayCode, templateCode }) => getConfiguredApiClient().applyGatewayTemplate(gatewayCode, templateCode),
        onSuccess: () => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.gateways(siteCode) });
            }
        },
    });
}
// =============================================================================
// Room Params Hooks (Site-scoped)
// =============================================================================
/** Room params; options as `useRoom` (overrides on top of the guard, no hidden-page poll). */
export function useRoomParams(roomCode, options) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.roomParams(siteCode || '', roomCode),
        queryFn: () => getConfiguredApiClient().getRoomParams(roomCode),
        refetchIntervalInBackground: false,
        ...options,
        enabled: !!siteCode && !!roomCode && options?.enabled !== false,
    });
}
/**
 * Hotel-relative thermostat zones of a room: the durable policy record (pending vs confirmed) with
 * observed values. Rooms without a bound zone return an empty list.
 */
export function useHotelThermostat(roomCode, options) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.hotelThermostat(siteCode || '', roomCode),
        queryFn: () => getConfiguredApiClient().getHotelThermostat(roomCode),
        refetchIntervalInBackground: false,
        ...options,
        enabled: !!siteCode && !!roomCode && options?.enabled !== false,
    });
}
export function useBindHotelThermostat() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ roomCode, zone, chCode }) => getConfiguredApiClient().bindHotelThermostat(roomCode, zone, chCode),
        onSuccess: (_data, variables) => {
            if (siteCode)
                queryClient.invalidateQueries({ queryKey: queryKeys.hotelThermostat(siteCode, variables.roomCode) });
        },
    });
}
export function useUnbindHotelThermostat() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ roomCode, zone }) => getConfiguredApiClient().unbindHotelThermostat(roomCode, zone),
        onSuccess: (_data, variables) => {
            if (siteCode)
                queryClient.invalidateQueries({ queryKey: queryKeys.hotelThermostat(siteCode, variables.roomCode) });
        },
    });
}
export function useRetryHotelThermostat() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ roomCode, zone }) => getConfiguredApiClient().retryHotelThermostat(roomCode, zone),
        onSuccess: (_data, variables) => {
            if (siteCode)
                queryClient.invalidateQueries({ queryKey: queryKeys.hotelThermostat(siteCode, variables.roomCode) });
        },
    });
}
export function useAcknowledgeHotelThermostat() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ roomCode, zone, discardOwedStay }) => getConfiguredApiClient().acknowledgeHotelThermostat(roomCode, zone, discardOwedStay ? { discardOwedStay } : {}),
        onSuccess: (_data, variables) => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.hotelThermostat(siteCode, variables.roomCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.roomParams(siteCode, variables.roomCode) });
            }
        },
    });
}
export function useUpdateRoomParams() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ roomCode, values }) => getConfiguredApiClient().updateRoomParams(roomCode, values),
        onSuccess: (_data, variables) => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.roomParams(siteCode, variables.roomCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.hotelThermostat(siteCode, variables.roomCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode) });
            }
        },
    });
}
export function useSetRoomCustomSchema() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ roomCode, params }) => getConfiguredApiClient().setRoomCustomSchema(roomCode, params),
        onSuccess: (_data, variables) => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.roomParams(siteCode, variables.roomCode) });
            }
        },
    });
}
export function useRoomBindings(roomCode) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.roomBindings(siteCode || '', roomCode),
        queryFn: () => getConfiguredApiClient().getRoomBindings(roomCode),
        enabled: !!siteCode && !!roomCode,
    });
}
export function useSetRoomBindings() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ roomCode, bindings }) => getConfiguredApiClient().setRoomBindings(roomCode, bindings),
        onSuccess: (_data, variables) => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.roomBindings(siteCode, variables.roomCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode) });
            }
        },
    });
}
export function useRoomBindingSuggestions(roomCode, enabled = true) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.roomBindingSuggestions(siteCode || '', roomCode),
        queryFn: () => getConfiguredApiClient().getRoomBindingSuggestions(roomCode),
        enabled: !!siteCode && !!roomCode && enabled,
    });
}
// =============================================================================
// Scene Hooks (Site-scoped)
// =============================================================================
export function useScenes(roomCode, options) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.scenes(siteCode || '', roomCode),
        queryFn: () => getConfiguredApiClient().getScenes(roomCode),
        enabled: !!siteCode && !!roomCode,
        ...options,
    });
}
export function useCreateScene() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ roomCode, params }) => getConfiguredApiClient().createScene(roomCode, params),
        onSuccess: (_, { roomCode }) => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.scenes(siteCode, roomCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.room(siteCode, roomCode) });
            }
        },
    });
}
export function useUpdateScene() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ roomCode, sceneCode, params }) => getConfiguredApiClient().updateScene(roomCode, sceneCode, params),
        onSuccess: (_, { roomCode }) => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.scenes(siteCode, roomCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.room(siteCode, roomCode) });
            }
        },
    });
}
export function useDeleteScene() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ roomCode, sceneCode }) => getConfiguredApiClient().deleteScene(roomCode, sceneCode),
        onSuccess: (_, { roomCode }) => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.scenes(siteCode, roomCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.room(siteCode, roomCode) });
            }
        },
    });
}
export function useActivateScene() {
    return useMutation({
        mutationFn: ({ roomCode, sceneCode }) => getConfiguredApiClient().activateScene(roomCode, sceneCode),
    });
}
// =============================================================================
// Channel Group Hooks (Site-scoped)
// =============================================================================
export function useUpdateRoomGroups() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ roomCode, groups }) => getConfiguredApiClient().updateRoomGroups(roomCode, groups),
        onSuccess: (_, { roomCode }) => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.room(siteCode, roomCode) });
            }
        },
    });
}
export function useSetChannelGroup() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ roomCode, channelId, groupCode, sortOrder, hidden }) => getConfiguredApiClient().setChannelGroup(roomCode, channelId, { groupCode, sortOrder, hidden }),
        onSuccess: (_, { roomCode }) => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.room(siteCode, roomCode) });
            }
        },
    });
}
export function useBulkUpdateChannelGroups() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ roomCode, assignments }) => getConfiguredApiClient().bulkUpdateChannelGroups(roomCode, assignments),
        onSuccess: (_, { roomCode }) => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.room(siteCode, roomCode) });
            }
        },
    });
}
// =============================================================================
// Bulk Import Hooks (Site-scoped)
// =============================================================================
export function usePreviewBulkImport() {
    return useMutation({
        mutationFn: (rows) => getConfiguredApiClient().previewBulkImport(rows),
    });
}
export function useExecuteBulkImport() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: (rows) => getConfiguredApiClient().executeBulkImport(rows),
        onSuccess: () => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode) });
            }
        },
    });
}
// =============================================================================
// Notification Hooks
// =============================================================================
export function useNotifications(params) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.notifications(siteCode ?? '', params),
        queryFn: () => getConfiguredApiClient().getNotifications(params),
        enabled: !!siteCode,
        refetchInterval: 30_000, // Refresh every 30s as backup to MQTT push
    });
}
export function useUnreadCount() {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.unreadCount(siteCode ?? ''),
        queryFn: () => getConfiguredApiClient().getUnreadCount(),
        enabled: !!siteCode,
        refetchInterval: 30_000,
    });
}
export function useMarkNotificationRead() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: (id) => getConfiguredApiClient().markNotificationRead(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications', siteCode ?? ''] });
        },
    });
}
export function useMarkAllNotificationsRead() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: () => getConfiguredApiClient().markAllNotificationsRead(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications', siteCode ?? ''] });
        },
    });
}
export function useDeleteNotification() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: (id) => getConfiguredApiClient().deleteNotification(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications', siteCode ?? ''] });
        },
    });
}
// =============================================================================
// Alert Rule Hooks
// =============================================================================
export function useAlertRules() {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.alertRules(siteCode ?? ''),
        queryFn: () => getConfiguredApiClient().getAlertRules(),
        enabled: !!siteCode,
    });
}
export function useCreateAlertRule() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: (params) => getConfiguredApiClient().createAlertRule(params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.alertRules(siteCode ?? '') });
        },
    });
}
export function useUpdateAlertRule() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ id, params }) => getConfiguredApiClient().updateAlertRule(id, params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.alertRules(siteCode ?? '') });
        },
    });
}
export function useDeleteAlertRule() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: (id) => getConfiguredApiClient().deleteAlertRule(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.alertRules(siteCode ?? '') });
        },
    });
}
// =============================================================================
// Access Control Hooks — Locks
// =============================================================================
export function useLocks(filters) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.locks(siteCode ?? '', filters),
        queryFn: () => getConfiguredApiClient().getLocks(filters),
        enabled: !!siteCode,
    });
}
export function useLockDetail(lockCode) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.lockDetail(siteCode ?? '', lockCode),
        queryFn: () => getConfiguredApiClient().getLock(lockCode),
        enabled: !!siteCode && !!lockCode,
    });
}
export function useCreateLock() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (params) => getConfiguredApiClient().createLock(params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.locks(siteCode ?? '') });
            // Room-bound locks surface on the room detail page (room.locks) — refresh it too.
            queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode ?? '') });
        },
    });
}
export function useUpdateLock() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ lockCode, params }) => getConfiguredApiClient().updateLock(lockCode, params),
        onSuccess: (_data, { lockCode }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.locks(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.lockDetail(siteCode ?? '', lockCode) });
            queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode ?? '') });
        },
    });
}
export function useDeleteLock() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (lockCode) => getConfiguredApiClient().deleteLock(lockCode),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.locks(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode ?? '') });
        },
    });
}
export function useLockLock() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (lockCode) => getConfiguredApiClient().lockLock(lockCode),
        onSuccess: (_data, lockCode) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.locks(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.lockDetail(siteCode ?? '', lockCode) });
        },
    });
}
export function useUnlockLock() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (lockCode) => getConfiguredApiClient().unlockLock(lockCode),
        onSuccess: (_data, lockCode) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.locks(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.lockDetail(siteCode ?? '', lockCode) });
        },
    });
}
/**
 * Momentary Open — a distinct route and a distinct meaning from held Unlock.
 *
 * BMS-spec `docs/edge/rest-api.md` §"Manual lock actions". No optimistic state:
 * a successful submission means the broker accepted the command, not that the
 * door moved, and a pulse establishes no permanent unlock. The mutation is
 * deliberately not retried — repeating an Open opens the door again.
 */
export function useOpenLock() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (lockCode) => getConfiguredApiClient().openLock(lockCode),
        retry: false,
        onSuccess: (_data, lockCode) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.locks(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.lockDetail(siteCode ?? '', lockCode) });
        },
    });
}
export function useEmergencyUnlock() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (scope) => getConfiguredApiClient().emergencyUnlock(scope),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.locks(siteCode ?? '') });
        },
    });
}
export function useLockEvents(filters) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.lockEvents(siteCode ?? '', filters),
        queryFn: () => getConfiguredApiClient().getLockEvents(filters),
        enabled: !!siteCode,
    });
}
export function useLockEventsByLock(lockCode, filters) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.lockEvents(siteCode ?? '', { ...filters, lockCode }),
        queryFn: () => getConfiguredApiClient().getLockEventsByLock(lockCode, filters),
        enabled: !!siteCode && !!lockCode,
    });
}
// =============================================================================
// Access Control Hooks — Zones (access-control-only locations)
// =============================================================================
export function useZones(filters) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.zones(siteCode ?? '', filters),
        queryFn: () => getConfiguredApiClient().getZones(filters),
        enabled: !!siteCode,
    });
}
export function useZone(zoneCode) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.zone(siteCode ?? '', zoneCode),
        queryFn: () => getConfiguredApiClient().getZone(zoneCode),
        enabled: !!siteCode && !!zoneCode,
    });
}
export function useCreateZone() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (params) => getConfiguredApiClient().createZone(params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.zones(siteCode ?? '') });
        },
    });
}
export function useUpdateZone() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ zoneCode, params }) => getConfiguredApiClient().updateZone(zoneCode, params),
        onSuccess: (_data, { zoneCode }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.zones(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.zone(siteCode ?? '', zoneCode) });
        },
    });
}
export function useDeleteZone() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (zoneCode) => getConfiguredApiClient().deleteZone(zoneCode),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.zones(siteCode ?? '') });
            // Locks/readers may have been detached (zone_id → null) on the backend.
            queryClient.invalidateQueries({ queryKey: queryKeys.locks(siteCode ?? '') });
        },
    });
}
// =============================================================================
// Access Zone Channels — the zone half of channel containment
// =============================================================================
export function useZoneChannels(zoneCode) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.zoneChannels(siteCode ?? '', zoneCode),
        queryFn: () => getConfiguredApiClient().getZoneChannels(zoneCode),
        enabled: !!siteCode && !!zoneCode,
    });
}
export function useZoneChannel(zoneCode, chCode) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.zoneChannel(siteCode ?? '', zoneCode, chCode),
        queryFn: () => getConfiguredApiClient().getZoneChannel(zoneCode, chCode),
        enabled: !!siteCode && !!zoneCode && !!chCode,
    });
}
export function useMapZoneChannels() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ zoneCode, request }) => getConfiguredApiClient().mapZoneChannels(zoneCode, request),
        onSuccess: (_data, { zoneCode }) => {
            if (!siteCode)
                return;
            queryClient.invalidateQueries({ queryKey: queryKeys.zone(siteCode, zoneCode) });
            queryClient.invalidateQueries({ queryKey: queryKeys.zoneChannels(siteCode, zoneCode) });
            // The channel may have come from a room, and its gateway's mapping column
            // shows the new container.
            queryClient.invalidateQueries({ queryKey: queryKeys.gateways(siteCode) });
            queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode) });
        },
    });
}
export function useUnmapZoneChannel() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ zoneCode, channelId }) => getConfiguredApiClient().unmapZoneChannel(zoneCode, channelId),
        onSuccess: (_data, { zoneCode }) => {
            if (!siteCode)
                return;
            queryClient.invalidateQueries({ queryKey: queryKeys.zone(siteCode, zoneCode) });
            queryClient.invalidateQueries({ queryKey: queryKeys.zoneChannels(siteCode, zoneCode) });
            queryClient.invalidateQueries({ queryKey: queryKeys.gateways(siteCode) });
        },
    });
}
export function useSendZoneChannelCommand() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ zoneCode, chCode, command }) => getConfiguredApiClient().sendZoneChannelCommand(zoneCode, chCode, command),
        onSuccess: (_data, { zoneCode, chCode }) => {
            if (!siteCode)
                return;
            queryClient.invalidateQueries({ queryKey: queryKeys.zoneChannel(siteCode, zoneCode, chCode) });
        },
    });
}
// =============================================================================
// Access Control — Readers (many-to-many reader-lock links)
// =============================================================================
export function useReaders() {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.readers(siteCode ?? ''),
        queryFn: () => getConfiguredApiClient().getReaders(),
        enabled: !!siteCode,
    });
}
export function useUpsertReader() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (params) => getConfiguredApiClient().upsertReader(params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.readers(siteCode ?? '') });
        },
    });
}
export function useReaderLocks(readerChannelId) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.readerLocks(siteCode ?? '', readerChannelId),
        queryFn: () => getConfiguredApiClient().getReaderLocks(readerChannelId),
        enabled: !!siteCode && !!readerChannelId,
    });
}
export function useLinkReaderToLock() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ readerChannelId, lockCode, role }) => getConfiguredApiClient().linkReaderToLock(readerChannelId, lockCode, role),
        onSuccess: (_data, { readerChannelId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.readers(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.readerLocks(siteCode ?? '', readerChannelId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.locks(siteCode ?? '') });
        },
    });
}
export function useUnlinkReaderFromLock() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ readerChannelId, lockCode, role }) => getConfiguredApiClient().unlinkReaderFromLock(readerChannelId, lockCode, role),
        onSuccess: (_data, { readerChannelId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.readers(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.readerLocks(siteCode ?? '', readerChannelId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.locks(siteCode ?? '') });
        },
    });
}
export function useLockReaders(lockCode) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.lockReaders(siteCode ?? '', lockCode),
        queryFn: () => getConfiguredApiClient().getLockReaders(lockCode),
        enabled: !!siteCode && !!lockCode,
    });
}
// =============================================================================
// Access Card Hooks
// =============================================================================
export function useAccessCards(filters) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.accessCards(siteCode ?? '', filters),
        queryFn: () => getConfiguredApiClient().getAccessCards(filters),
        enabled: !!siteCode,
    });
}
/** Active guest keys per room (`GET /access/cards/guest-key-summary`); pass `enabled: false` without `cards:read`. */
export function useGuestKeySummary(options = {}) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.guestKeySummary(siteCode ?? ''),
        queryFn: () => getConfiguredApiClient().getGuestKeySummary(),
        enabled: !!siteCode && (options.enabled ?? true),
    });
}
export function useAccessCardDetail(cardId) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.accessCardDetail(siteCode ?? '', cardId),
        queryFn: () => getConfiguredApiClient().getAccessCard(cardId),
        enabled: !!siteCode && !!cardId,
    });
}
export function useIssueCard() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (params) => getConfiguredApiClient().issueCard(params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.accessCards(siteCode ?? '') });
        },
    });
}
export function useGetIssueCardPayload() {
    return useMutation({
        mutationFn: (params) => getConfiguredApiClient().getIssueCardPayload(params),
    });
}
export function useReplaceCard() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ cardId, params }) => getConfiguredApiClient().replaceCard(cardId, params),
        onSuccess: (_data, { cardId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.accessCards(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.accessCardDetail(siteCode ?? '', cardId) });
        },
    });
}
export function useGetReplaceCardPayload() {
    return useMutation({
        mutationFn: ({ cardId, params }) => getConfiguredApiClient().getReplaceCardPayload(cardId, params),
    });
}
export function useUpdateCard() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ cardId, params }) => getConfiguredApiClient().updateCard(cardId, params),
        onSuccess: (_data, { cardId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.accessCards(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.accessCardDetail(siteCode ?? '', cardId) });
        },
    });
}
export function useRevokeCard() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ cardId, reason }) => getConfiguredApiClient().revokeCard(cardId, reason),
        onSuccess: (_data, { cardId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.accessCards(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.accessCardDetail(siteCode ?? '', cardId) });
        },
    });
}
export function useSuspendCard() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ cardId, reason }) => getConfiguredApiClient().suspendCard(cardId, reason),
        onSuccess: (_data, { cardId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.accessCards(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.accessCardDetail(siteCode ?? '', cardId) });
        },
    });
}
export function useReactivateCard() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (cardId) => getConfiguredApiClient().reactivateCard(cardId),
        onSuccess: (_data, cardId) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.accessCards(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.accessCardDetail(siteCode ?? '', cardId) });
        },
    });
}
export function useDeleteCard() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (cardId) => getConfiguredApiClient().retireCard(cardId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.accessCards(siteCode ?? '') });
        },
    });
}
export function useReportLostCard() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (cardId) => getConfiguredApiClient().reportLostCard(cardId),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.accessCards(siteCode ?? '') }),
    });
}
export function useConfirmCardWritten() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ cardId, writeReport }) => getConfiguredApiClient().confirmCardWritten(cardId, writeReport),
        onSuccess: (_data, { cardId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.accessCards(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.accessCardDetail(siteCode ?? '', cardId) });
        },
    });
}
export function useGetCardPayload() {
    return useMutation({
        mutationFn: ({ cardId, chipType }) => getConfiguredApiClient().getCardPayload(cardId, chipType),
    });
}
export function useRoomCards(roomCode) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.roomCards(siteCode ?? '', roomCode),
        queryFn: () => getConfiguredApiClient().getRoomCards(roomCode),
        enabled: !!siteCode && !!roomCode,
    });
}
export function useRevokeAllRoomCards() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ roomCode, reason }) => getConfiguredApiClient().revokeAllRoomCards(roomCode, reason),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.accessCards(siteCode ?? '') });
        },
    });
}
// =============================================================================
// Card Role Template Hooks
// =============================================================================
export function useCardRoles() {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.cardRoles(siteCode ?? ''),
        queryFn: () => getConfiguredApiClient().getCardRoles(),
        enabled: !!siteCode,
    });
}
export function useCardRole(roleName) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.cardRole(siteCode ?? '', roleName),
        queryFn: () => getConfiguredApiClient().getCardRole(roleName),
        enabled: !!siteCode && !!roleName,
    });
}
export function useCreateCardRole() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (params) => getConfiguredApiClient().createCardRole(params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.cardRoles(siteCode ?? '') });
        },
    });
}
export function useUpdateCardRole() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ roleName, params }) => getConfiguredApiClient().updateCardRole(roleName, params),
        onSuccess: (_data, { roleName }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.cardRoles(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.cardRole(siteCode ?? '', roleName) });
        },
    });
}
export function useDeleteCardRole() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (roleName) => getConfiguredApiClient().deleteCardRole(roleName),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.cardRoles(siteCode ?? '') });
        },
    });
}
export function useApplyRole() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (roleName) => getConfiguredApiClient().applyRole(roleName),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.accessCards(siteCode ?? '') });
        },
    });
}
// =============================================================================
// Lock Group Hooks
// =============================================================================
export function useLockGroups() {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.lockGroups(siteCode ?? ''),
        queryFn: () => getConfiguredApiClient().getLockGroups(),
        enabled: !!siteCode,
    });
}
export function useLockGroup(groupCode) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.lockGroup(siteCode ?? '', groupCode),
        queryFn: () => getConfiguredApiClient().getLockGroup(groupCode),
        enabled: !!siteCode && !!groupCode,
    });
}
export function useCreateLockGroup() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (params) => getConfiguredApiClient().createLockGroup(params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.lockGroups(siteCode ?? '') });
        },
    });
}
export function useUpdateLockGroup() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ groupCode, params }) => getConfiguredApiClient().updateLockGroup(groupCode, params),
        onSuccess: (_data, { groupCode }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.lockGroups(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.lockGroup(siteCode ?? '', groupCode) });
        },
    });
}
export function useDeleteLockGroup() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (groupCode) => getConfiguredApiClient().deleteLockGroup(groupCode),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.lockGroups(siteCode ?? '') });
        },
    });
}
export function useAddLocksToGroup() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ groupCode, lockCodes }) => getConfiguredApiClient().addLocksToGroup(groupCode, lockCodes),
        onSuccess: (_data, { groupCode }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.lockGroups(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.lockGroup(siteCode ?? '', groupCode) });
        },
    });
}
export function useRemoveLocksFromGroup() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ groupCode, lockCodes }) => getConfiguredApiClient().removeLocksFromGroup(groupCode, lockCodes),
        onSuccess: (_data, { groupCode }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.lockGroups(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.lockGroup(siteCode ?? '', groupCode) });
        },
    });
}
// =============================================================================
// Access Schedule Hooks
//
// Recurring weekly access windows. NOT the cron automation hooks further down:
// that mechanism fires channel commands at an instant and reaches no gateway.
// =============================================================================
export function useAccessSchedules() {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.accessSchedules(siteCode ?? ''),
        queryFn: () => getConfiguredApiClient().getAccessSchedules(),
        enabled: !!siteCode,
    });
}
export function useAccessSchedule(scheduleCode) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.accessSchedule(siteCode ?? '', scheduleCode),
        queryFn: () => getConfiguredApiClient().getAccessSchedule(scheduleCode),
        enabled: !!siteCode && !!scheduleCode,
    });
}
export function useCreateAccessSchedule() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (params) => getConfiguredApiClient().createAccessSchedule(params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.accessSchedules(siteCode ?? '') });
        },
    });
}
export function useUpdateAccessSchedule() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ scheduleCode, ...params }) => getConfiguredApiClient().updateAccessSchedule(scheduleCode, params),
        onSuccess: (_data, { scheduleCode }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.accessSchedules(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.accessSchedule(siteCode ?? '', scheduleCode) });
        },
    });
}
export function useDeleteAccessSchedule() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (scheduleCode) => getConfiguredApiClient().deleteAccessSchedule(scheduleCode),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.accessSchedules(siteCode ?? '') });
        },
    });
}
/**
 * Replace only a schedule's windows — the front office manager's edit.
 *
 * Beside `useUpdateAccessSchedule` rather than instead of it: the two reach
 * different permissions and different routes, and a page that holds only
 * `access_control:write_schedule_windows` must not be able to call the wide one
 * by accident. Both invalidate the same two keys, because the detail carries
 * the window list the list projection also shows.
 */
export function useUpdateAccessScheduleWindows() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ scheduleCode, windows }) => getConfiguredApiClient().updateAccessScheduleWindows(scheduleCode, windows),
        onSuccess: (_data, { scheduleCode }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.accessSchedules(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.accessSchedule(siteCode ?? '', scheduleCode) });
        },
    });
}
export function useLockSchedules(lockCode) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.lockSchedules(siteCode ?? '', lockCode),
        queryFn: () => getConfiguredApiClient().getLockSchedules(lockCode),
        enabled: !!siteCode && !!lockCode,
    });
}
/** Full-state replacement; the request republishes reader context before it resolves. */
export function useSetLockSchedules() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ lockCode, attachments }) => getConfiguredApiClient().setLockSchedules(lockCode, attachments),
        onSuccess: (_data, { lockCode }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.lockSchedules(siteCode ?? '', lockCode) });
            queryClient.invalidateQueries({ queryKey: queryKeys.accessSchedules(siteCode ?? '') });
        },
    });
}
export function useCardSchedules(cardId) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.cardSchedules(siteCode ?? '', cardId),
        queryFn: () => getConfiguredApiClient().getCardSchedules(cardId),
        enabled: !!siteCode && !!cardId,
    });
}
export function useSetCardSchedules() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ cardId, scheduleCodes }) => getConfiguredApiClient().setCardSchedules(cardId, scheduleCodes),
        onSuccess: (_data, { cardId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.cardSchedules(siteCode ?? '', cardId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.accessSchedules(siteCode ?? '') });
        },
    });
}
/**
 * Totals for a range: grants, denials, grant rate, denial breakdown, busiest
 * locks and hours. BMS-spec docs/access/analytics.md §Analytics API; the route
 * is `access_analytics:read`, which is a narrower permission than
 * `access_control:read`, so a caller who may see the Events log may still be
 * refused this. `retry: false` keeps a 403 from being asked for three times,
 * and the caller renders nothing rather than an error when it is.
 */
export function useAccessAnalyticsSummary(query = {}, options) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.accessAnalyticsSummary(siteCode ?? '', query),
        queryFn: () => getConfiguredApiClient().getAccessAnalyticsSummary(query),
        enabled: !!siteCode,
        retry: false,
        ...options,
    });
}
// =============================================================================
// Visitor Hooks
//
// Two entities: the person (`visitors`) and the occasion (`visits`). Every read
// carries `effectiveStatus` — `no_show` and `overstayed` are derived from the
// clock at read time and never stored, so a screen shows that and never a value
// it computed for itself.
//
// The visitor's card is issued with the ordinary card hooks and `visitId` in the
// body. Check-out revokes the visit's live cards; retiring them is the separate
// act the desk performs when the card is reused.
// =============================================================================
export function useVisitors(filters) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.visitors(siteCode ?? '', filters),
        queryFn: () => getConfiguredApiClient().getVisitors(filters),
        enabled: !!siteCode,
    });
}
export function useVisitor(visitorId) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.visitor(siteCode ?? '', visitorId),
        queryFn: () => getConfiguredApiClient().getVisitor(visitorId),
        enabled: !!siteCode && !!visitorId,
    });
}
export function useCreateVisitor() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (params) => getConfiguredApiClient().createVisitor(params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['visitors', siteCode ?? ''] });
        },
    });
}
export function useUpdateVisitor() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ visitorId, ...params }) => getConfiguredApiClient().updateVisitor(visitorId, params),
        onSuccess: (_data, { visitorId }) => {
            queryClient.invalidateQueries({ queryKey: ['visitors', siteCode ?? ''] });
            queryClient.invalidateQueries({ queryKey: queryKeys.visitor(siteCode ?? '', visitorId) });
            // A deny blocks new visits, so any list showing them is now stale.
            queryClient.invalidateQueries({ queryKey: ['visits', siteCode ?? ''] });
        },
    });
}
/** Refused with `visit_has_live_cards` while the person holds a non-retired card. */
export function useEraseVisitor() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (visitorId) => getConfiguredApiClient().eraseVisitor(visitorId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['visitors', siteCode ?? ''] });
            queryClient.invalidateQueries({ queryKey: ['visits', siteCode ?? ''] });
            // The linked retired card records had their holder name scrubbed.
            queryClient.invalidateQueries({ queryKey: ['accessCards', siteCode ?? ''] });
        },
    });
}
export function useVisits(filters) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.visits(siteCode ?? '', filters),
        queryFn: () => getConfiguredApiClient().getVisits(filters),
        enabled: !!siteCode,
    });
}
export function useVisit(visitId) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.visit(siteCode ?? '', visitId),
        queryFn: () => getConfiguredApiClient().getVisit(visitId),
        enabled: !!siteCode && !!visitId,
    });
}
export function useCreateVisit() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (params) => getConfiguredApiClient().createVisit(params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['visits', siteCode ?? ''] });
            queryClient.invalidateQueries({ queryKey: ['visitors', siteCode ?? ''] });
        },
    });
}
/**
 * Extending a visit does NOT extend a card. Widening the credential is
 * `useUpdateCard({ validUntil })`, which comes back with `requiresRewrite: true`
 * and needs the card back at an encoder.
 */
export function useUpdateVisit() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ visitId, ...params }) => getConfiguredApiClient().updateVisit(visitId, params),
        onSuccess: (_data, { visitId }) => {
            queryClient.invalidateQueries({ queryKey: ['visits', siteCode ?? ''] });
            queryClient.invalidateQueries({ queryKey: queryKeys.visit(siteCode ?? '', visitId) });
        },
    });
}
export function useCheckInVisit() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (visitId) => getConfiguredApiClient().checkInVisit(visitId),
        onSuccess: (_data, visitId) => {
            queryClient.invalidateQueries({ queryKey: ['visits', siteCode ?? ''] });
            queryClient.invalidateQueries({ queryKey: queryKeys.visit(siteCode ?? '', visitId) });
        },
    });
}
/** Revokes the visit's live cards, which is why the card lists go stale too. */
export function useCheckOutVisit() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (visitId) => getConfiguredApiClient().checkOutVisit(visitId),
        onSuccess: (_data, visitId) => {
            queryClient.invalidateQueries({ queryKey: ['visits', siteCode ?? ''] });
            queryClient.invalidateQueries({ queryKey: queryKeys.visit(siteCode ?? '', visitId) });
            queryClient.invalidateQueries({ queryKey: ['accessCards', siteCode ?? ''] });
        },
    });
}
export function useCancelVisit() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (visitId) => getConfiguredApiClient().cancelVisit(visitId),
        onSuccess: (_data, visitId) => {
            queryClient.invalidateQueries({ queryKey: ['visits', siteCode ?? ''] });
            queryClient.invalidateQueries({ queryKey: queryKeys.visit(siteCode ?? '', visitId) });
        },
    });
}
// =============================================================================
// Schedule Hooks (cron automation)
// =============================================================================
export function useSchedules() {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.schedules(siteCode ?? ''),
        queryFn: () => getConfiguredApiClient().getSchedules(),
        enabled: !!siteCode,
    });
}
export function useSchedule(id) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.schedule(siteCode ?? '', id),
        queryFn: () => getConfiguredApiClient().getSchedule(id),
        enabled: !!siteCode && !!id,
    });
}
export function useCreateSchedule() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (params) => getConfiguredApiClient().createSchedule(params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.schedules(siteCode ?? '') });
        },
    });
}
export function useUpdateSchedule() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ id, ...params }) => getConfiguredApiClient().updateSchedule(id, params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.schedules(siteCode ?? '') });
        },
    });
}
// =============================================================================
// GRMS Hooks
// =============================================================================
/** Quiet period before the desk summaries refetch after a room broadcast. */
export const GRMS_SUMMARY_REFRESH_DEBOUNCE_MS = 500;
const GRMS_SUMMARY_REFRESH_MAX_WAIT_MS = 1000;
// One subscription and one debounce per QueryClient and site, however many
// summary hooks are mounted (the sidebar's stays board, the housekeeping
// header, ...) — so one broadcast is one refetch of each summary.
const grmsSummaryLive = new WeakMap();
function acquireGrmsSummaryLive(queryClient, siteCode) {
    let bySite = grmsSummaryLive.get(queryClient);
    if (!bySite) {
        bySite = new Map();
        grmsSummaryLive.set(queryClient, bySite);
    }
    let entry = bySite.get(siteCode);
    if (!entry) {
        const keys = [
            queryKeys.occupancySummary(siteCode),
            queryKeys.todayStays(siteCode),
            queryKeys.housekeepingSummary(siteCode),
        ];
        const refresh = createTrailingDebounce(() => {
            for (const queryKey of keys)
                void queryClient.invalidateQueries({ queryKey });
        }, GRMS_SUMMARY_REFRESH_DEBOUNCE_MS, GRMS_SUMMARY_REFRESH_MAX_WAIT_MS);
        const unsubscribe = subscribeShared([buildSiteRoomsTopicFilter(siteCode, 'occupancy'), buildSiteRoomsTopicFilter(siteCode, 'housekeeping')], () => refresh.schedule());
        entry = { refs: 0, release: () => { refresh.cancel(); unsubscribe(); } };
        bySite.set(siteCode, entry);
    }
    const held = entry;
    held.refs++;
    return () => {
        held.refs--;
        if (held.refs > 0)
            return;
        held.release();
        bySite.delete(siteCode);
    };
}
/**
 * Keep the desk summaries — occupancy, today's stays, housekeeping — live on
 * every page that shows one: a `/occupancy` or `/housekeeping` broadcast
 * refetches all three, debounced 500 ms (at most 1 s), so a check-in on one
 * desk reaches another's stays board in about a second (GRMS §11.3). The 30 s
 * poll stays as the safety net.
 */
export function useGrmsSummariesLive(siteCode) {
    const queryClient = useQueryClient();
    useEffect(() => {
        if (!siteCode)
            return;
        return acquireGrmsSummaryLive(queryClient, siteCode);
    }, [queryClient, siteCode]);
}
export function useOccupancySummary() {
    const siteCode = useAuthStore.getState().currentSiteCode;
    useGrmsSummariesLive(siteCode);
    return useQuery({
        queryKey: queryKeys.occupancySummary(siteCode ?? ''),
        queryFn: () => getConfiguredApiClient().getOccupancySummary(),
        enabled: !!siteCode,
        refetchInterval: 30_000,
    });
}
export function useTodayStays() {
    const siteCode = useAuthStore.getState().currentSiteCode;
    useGrmsSummariesLive(siteCode);
    return useQuery({
        queryKey: queryKeys.todayStays(siteCode ?? ''),
        queryFn: () => getConfiguredApiClient().getTodayStays(),
        enabled: !!siteCode,
        refetchInterval: 30_000,
    });
}
export function useUpdateOccupancy() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ roomCode, ...params }) => getConfiguredApiClient().updateOccupancy(roomCode, params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.occupancySummary(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.todayStays(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode ?? '') });
            // A check-out revokes the stay's keys and a check-in may issue one, so the
            // room dialog's key count (under this prefix) refreshes on every change.
            queryClient.invalidateQueries({ queryKey: queryKeys.accessCards(siteCode ?? '') });
        },
    });
}
export function useMoveStay() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ roomCode, targetRoomCode, }) => getConfiguredApiClient().moveStay(roomCode, targetRoomCode),
        onSuccess: (_data, { roomCode, targetRoomCode }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.occupancySummary(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.todayStays(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.roomCards(siteCode ?? '', roomCode) });
            queryClient.invalidateQueries({ queryKey: queryKeys.roomCards(siteCode ?? '', targetRoomCode) });
            queryClient.invalidateQueries({ queryKey: ['accessCards'] });
        },
    });
}
/** Add a note to a room's log; the room's events refetch (and `/events` reaches other desks). */
export function useAddRoomNote() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ roomCode, note }) => getConfiguredApiClient().addRoomNote(roomCode, note),
        onSuccess: (_data, { roomCode }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.roomEvents(siteCode ?? '', roomCode) });
        },
    });
}
export function useRoomEvents(roomCode, params, options) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: [...queryKeys.roomEvents(siteCode ?? '', roomCode), params ?? {}],
        queryFn: () => getConfiguredApiClient().getRoomEvents(roomCode, params),
        enabled: !!siteCode && !!roomCode && (options?.enabled ?? true),
        ...options,
    });
}
export function useRoomHistory(roomCode, params, options) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: [...queryKeys.roomHistory(siteCode ?? '', roomCode), params ?? {}],
        queryFn: () => getConfiguredApiClient().getRoomHistory(roomCode, params),
        enabled: !!siteCode && !!roomCode,
        ...options,
    });
}
export function useGrmsAnalytics(days = 30, options) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.grmsAnalytics(siteCode ?? '', days),
        queryFn: () => getConfiguredApiClient().getGrmsAnalytics(days),
        enabled: !!siteCode,
        staleTime: 60_000,
        ...options,
    });
}
export function usePmsConnection(options) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.pmsConnection(siteCode ?? ''),
        queryFn: () => getConfiguredApiClient().getPmsConnection(),
        enabled: !!siteCode,
        ...options,
    });
}
export function useUpdatePmsConnection() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (update) => getConfiguredApiClient().updatePmsConnection(update),
        onSuccess: (data) => {
            queryClient.setQueryData(queryKeys.pmsConnection(siteCode ?? ''), data);
        },
    });
}
export function useUpdateHousekeeping() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ roomCode, ...params }) => getConfiguredApiClient().updateHousekeeping(roomCode, params),
        onSuccess: (_data, { roomCode }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.occupancySummary(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.housekeepingSummary(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.roomEvents(siteCode ?? '', roomCode) });
            queryClient.invalidateQueries({ queryKey: ['events'] });
        },
    });
}
export function useReportMaintenanceIssue() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ roomCode, ...params }) => getConfiguredApiClient().reportMaintenanceIssue(roomCode, params),
        onSuccess: (_data, { roomCode }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.occupancySummary(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.housekeepingSummary(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.roomEvents(siteCode ?? '', roomCode) });
            queryClient.invalidateQueries({ queryKey: ['events'] });
        },
    });
}
/**
 * Acknowledges a Make Up Room request (GRMS spec section 11.4). The response is
 * 202 `pending_hardware_clear`: the flag itself only drops when the room's own
 * hardware reports false, so nothing here writes `murActive`. The room queries
 * are invalidated for the `mur_acknowledged` event and the clear-pending flag
 * the acknowledgement raises.
 */
export function useAcknowledgeMur() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ roomCode }) => getConfiguredApiClient().acknowledgeMur(roomCode),
        onSuccess: (_data, { roomCode }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.occupancySummary(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.housekeepingSummary(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.roomEvents(siteCode ?? '', roomCode) });
            queryClient.invalidateQueries({ queryKey: ['events'] });
        },
    });
}
/**
 * Sets or clears the service overlay (§6.3). The occupancy endpoint no longer
 * accepts the overlay values, so this is the only way to mark a room out of
 * order / out of service or restore it.
 */
export function useUpdateServiceState() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ roomCode, ...params }) => getConfiguredApiClient().updateServiceState(roomCode, params),
        onSuccess: (_data, { roomCode }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.occupancySummary(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.housekeepingSummary(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.rooms(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.roomEvents(siteCode ?? '', roomCode) });
            queryClient.invalidateQueries({ queryKey: ['events'] });
        },
    });
}
export function useHousekeepingSummary() {
    const siteCode = useAuthStore.getState().currentSiteCode;
    useGrmsSummariesLive(siteCode);
    return useQuery({
        queryKey: queryKeys.housekeepingSummary(siteCode ?? ''),
        queryFn: () => getConfiguredApiClient().getHousekeepingSummary(),
        enabled: !!siteCode,
        refetchInterval: 30_000,
    });
}
export function useBatchAssignHousekeeping() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (params) => getConfiguredApiClient().batchAssignHousekeeping(params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.housekeepingSummary(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.occupancySummary(siteCode ?? '') });
        },
    });
}
// =============================================================================
// Alarm Hooks
// =============================================================================
export function useActiveAlarms() {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.activeAlarms(siteCode ?? ''),
        queryFn: () => getConfiguredApiClient().getActiveAlarms(),
        enabled: !!siteCode,
        refetchInterval: 15_000,
    });
}
/**
 * A room's alarms, polled every 15 s by default; the poll pauses while the
 * document is hidden, and `options` can pause it further (`enabled`) or retime
 * it (`refetchInterval`).
 */
export function useRoomAlarms(roomCode, options) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.roomAlarms(siteCode ?? '', roomCode),
        queryFn: () => getConfiguredApiClient().getRoomAlarms(roomCode),
        refetchInterval: 15_000,
        refetchIntervalInBackground: false,
        ...options,
        enabled: !!siteCode && !!roomCode && options?.enabled !== false,
    });
}
/**
 * Alarm history for the selected site. Unlike `useActiveAlarms` this reads the
 * closed rows too and is paged, so it does not poll: a range that has already
 * happened does not change under the reader the way an open alarm list does.
 */
export function useAlarmHistory(params, options) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.alarmHistory(siteCode ?? '', params),
        queryFn: () => getConfiguredApiClient().getAlarmHistory(params),
        enabled: !!siteCode && (options?.enabled ?? true),
        ...options,
    });
}
export function useAcknowledgeAlarm() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (alarmId) => getConfiguredApiClient().acknowledgeAlarm(alarmId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.activeAlarms(siteCode ?? '') });
            // Also invalidate any open room alarm queries
            queryClient.invalidateQueries({ queryKey: ['alarms', siteCode ?? ''], exact: false });
        },
    });
}
export function useResolveAlarm() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (alarmId) => getConfiguredApiClient().resolveAlarm(alarmId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.activeAlarms(siteCode ?? '') });
            // Also invalidate any open room alarm queries
            queryClient.invalidateQueries({ queryKey: ['alarms', siteCode ?? ''], exact: false });
        },
    });
}
export function useToggleSchedule() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ id, enabled }) => getConfiguredApiClient().toggleSchedule(id, enabled),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.schedules(siteCode ?? '') });
        },
    });
}
export function useDeleteSchedule() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (id) => getConfiguredApiClient().deleteSchedule(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.schedules(siteCode ?? '') });
        },
    });
}
// =============================================================================
// Firmware / OTA Hooks
// =============================================================================
export function useFirmwareList(product) {
    return useQuery({
        queryKey: queryKeys.firmwareList(product),
        queryFn: () => getConfiguredApiClient().getFirmwareList(product),
    });
}
export function useFirmware(id) {
    return useQuery({
        queryKey: queryKeys.firmware(id),
        queryFn: () => getConfiguredApiClient().getFirmware(id),
        enabled: !!id,
    });
}
export function useUploadFirmware() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ file, version, product, releaseNotes }) => getConfiguredApiClient().uploadFirmware(file, version, product, releaseNotes),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['firmware'] });
        },
    });
}
export function useDeleteFirmware() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => getConfiguredApiClient().deleteFirmware(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['firmware'] });
        },
    });
}
export function useDeployFirmware() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: ({ firmwareId, ...params }) => getConfiguredApiClient().deployFirmware(firmwareId, params),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.deployments(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.versionMatrix(siteCode ?? '') });
        },
    });
}
export function useDeployments(filters) {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.deployments(siteCode ?? '', filters),
        queryFn: () => getConfiguredApiClient().getDeployments(filters),
        enabled: !!siteCode,
        refetchInterval: 15000, // 15s fallback polling — real-time updates via MQTT WebSocket
    });
}
export function useCancelDeployment() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useMutation({
        mutationFn: (deploymentId) => getConfiguredApiClient().cancelDeployment(deploymentId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.deployments(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: queryKeys.versionMatrix(siteCode ?? '') });
        },
    });
}
export function useVersionMatrix() {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.versionMatrix(siteCode ?? ''),
        queryFn: () => getConfiguredApiClient().getVersionMatrix(),
        enabled: !!siteCode,
        refetchInterval: 30000,
    });
}
// =============================================================================
// Floor Plan Hooks
// =============================================================================
export function useRoomLayouts(type, enabled = true) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: [...queryKeys.roomLayouts(siteCode || ''), type ?? 'all'],
        queryFn: () => getConfiguredApiClient().getRoomLayouts(type),
        enabled: !!siteCode && enabled,
    });
}
export function useRoomLayout(templateCode) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: queryKeys.roomLayout(siteCode || '', templateCode),
        queryFn: () => getConfiguredApiClient().getRoomLayout(templateCode),
        enabled: !!siteCode && !!templateCode,
    });
}
export function useCreateRoomLayout() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: (params) => getConfiguredApiClient().createRoomLayout(params),
        onSuccess: () => {
            if (siteCode)
                queryClient.invalidateQueries({ queryKey: queryKeys.roomLayouts(siteCode) });
        },
    });
}
export function useUpdateRoomLayout() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ code, params }) => getConfiguredApiClient().updateRoomLayout(code, params),
        onSuccess: (_, { code }) => {
            if (siteCode) {
                queryClient.invalidateQueries({ queryKey: queryKeys.roomLayouts(siteCode) });
                queryClient.invalidateQueries({ queryKey: queryKeys.roomLayout(siteCode, code) });
            }
        },
    });
}
export function useDeleteRoomLayout() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: (code) => getConfiguredApiClient().deleteRoomLayout(code),
        onSuccess: () => {
            if (siteCode)
                queryClient.invalidateQueries({ queryKey: queryKeys.roomLayouts(siteCode) });
        },
    });
}
export function useDuplicateRoomLayout() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: ({ code, newName }) => getConfiguredApiClient().duplicateRoomLayout(code, newName),
        onSuccess: () => {
            if (siteCode)
                queryClient.invalidateQueries({ queryKey: queryKeys.roomLayouts(siteCode) });
        },
    });
}
export function useImportRoomLayout() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: (yaml) => getConfiguredApiClient().importRoomLayout(yaml),
        onSuccess: () => {
            if (siteCode)
                queryClient.invalidateQueries({ queryKey: queryKeys.roomLayouts(siteCode) });
        },
    });
}
export function useExportRoomLayout() {
    return useMutation({
        mutationFn: (code) => getConfiguredApiClient().exportRoomLayout(code),
    });
}
export function useSamMaterial() {
    const siteCode = useAuthStore.getState().currentSiteCode;
    return useQuery({
        queryKey: queryKeys.samMaterial(siteCode ?? ''),
        queryFn: () => getConfiguredApiClient().getSamMaterial(),
        enabled: !!siteCode,
    });
}
export function useServerSigningKeys() {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useQuery({
        queryKey: ['serverSigningKeys', siteCode],
        queryFn: () => getConfiguredApiClient().getServerSigningKeys(),
        enabled: !!siteCode,
    });
}
export function useImportSitePackage() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: (body) => getConfiguredApiClient().importSitePackage(body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.samMaterial(siteCode ?? '') });
            queryClient.invalidateQueries({ queryKey: ['serverSigningKeys', siteCode] });
            queryClient.invalidateQueries({ queryKey: queryKeys.siteConfig });
        },
    });
}
/**
 * "Activate a site" on an installation with no site. The caller then selects the
 * new site with its pending token (BMS-spec docs/edge/rest-api.md
 * §POST /api/access/site-activation).
 */
export function useActivateInstallationSite() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body) => getConfiguredApiClient().activateInstallationSite(body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.sites });
            queryClient.invalidateQueries({ queryKey: queryKeys.siteConfig });
        },
    });
}
/** "Back up now" beside the site-file check. */
export function useBackupSiteConfiguration() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => getConfiguredApiClient().backupSiteConfiguration(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.siteConfig });
        },
        onError: () => {
            // A refusal is recorded in the site settings too; show it.
            queryClient.invalidateQueries({ queryKey: queryKeys.siteConfig });
        },
    });
}
export function useCheckSitePackage() {
    const queryClient = useQueryClient();
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    return useMutation({
        mutationFn: () => getConfiguredApiClient().checkSitePackage(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.siteConfig });
            queryClient.invalidateQueries({ queryKey: ['serverSigningKeys', siteCode] });
        },
    });
}
// =============================================================================
// Virtual Devices (room emulator control plane) — /dev/virtual/*
//
// Three transport states that are not failures (plan §2.4): unset
// `VIRTUAL_EMULATOR_URL` (`emulator_not_configured`), a configured but
// unreachable emulator (`emulator_unreachable`), and the ordinary success
// path. Every query here therefore disables react-query's retry — retrying a
// "the emulator isn't running" answer just delays the page from being able
// to render that state — and lets the page read the classified error off
// `query.error` itself (see `classifyVirtualDeviceError` in the Admin page).
// =============================================================================
export function useVirtualRoutes(options) {
    return useQuery({
        queryKey: queryKeys.virtualRoutes,
        queryFn: () => getConfiguredApiClient().getVirtualRoutes(),
        retry: false,
        ...options,
    });
}
export function useVirtualHealth(options) {
    return useQuery({
        queryKey: queryKeys.virtualHealth,
        queryFn: () => getConfiguredApiClient().getVirtualHealth(),
        retry: false,
        refetchInterval: 15_000,
        ...options,
    });
}
export function useVirtualProfiles(options) {
    return useQuery({
        queryKey: queryKeys.virtualProfiles,
        queryFn: () => getConfiguredApiClient().getVirtualProfiles(),
        retry: false,
        ...options,
    });
}
export function useVirtualScenarios(options) {
    return useQuery({
        queryKey: queryKeys.virtualScenarios,
        queryFn: () => getConfiguredApiClient().getVirtualScenarios(),
        retry: false,
        ...options,
    });
}
export function useVirtualInstances(options) {
    return useQuery({
        queryKey: queryKeys.virtualInstances,
        queryFn: () => getConfiguredApiClient().getVirtualInstances(),
        retry: false,
        refetchInterval: 5_000,
        ...options,
    });
}
export function useVirtualInstance(hwUid, options) {
    return useQuery({
        queryKey: queryKeys.virtualInstance(hwUid),
        queryFn: () => getConfiguredApiClient().getVirtualInstance(hwUid),
        enabled: !!hwUid,
        retry: false,
        refetchInterval: 5_000,
        ...options,
    });
}
function invalidateVirtualInstances(queryClient) {
    queryClient.invalidateQueries({ queryKey: queryKeys.virtualInstances });
    queryClient.invalidateQueries({ queryKey: queryKeys.virtualHealth });
}
export function useAddVirtualInstance() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (params) => getConfiguredApiClient().addVirtualInstance(params),
        onSuccess: () => invalidateVirtualInstances(queryClient),
    });
}
export function useBatchAddVirtualInstances() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (params) => getConfiguredApiClient().batchAddVirtualInstances(params),
        onSuccess: () => invalidateVirtualInstances(queryClient),
    });
}
export function useModifyVirtualInstance() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ hwUid, params }) => getConfiguredApiClient().modifyVirtualInstance(hwUid, params),
        onSuccess: (_data, { hwUid }) => {
            invalidateVirtualInstances(queryClient);
            queryClient.invalidateQueries({ queryKey: queryKeys.virtualInstance(hwUid) });
        },
    });
}
export function useRemoveVirtualInstance() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ hwUid, force }) => getConfiguredApiClient().removeVirtualInstance(hwUid, force),
        onSuccess: () => invalidateVirtualInstances(queryClient),
    });
}
export function useRemoveAllVirtualInstances() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (params) => getConfiguredApiClient().removeAllVirtualInstances(params?.force),
        onSuccess: () => invalidateVirtualInstances(queryClient),
    });
}
export function useStartVirtualInstance() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (hwUid) => getConfiguredApiClient().startVirtualInstance(hwUid),
        onSuccess: () => invalidateVirtualInstances(queryClient),
    });
}
export function useStopVirtualInstance() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (hwUid) => getConfiguredApiClient().stopVirtualInstance(hwUid),
        onSuccess: () => invalidateVirtualInstances(queryClient),
    });
}
export function useRestartVirtualInstance() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (hwUid) => getConfiguredApiClient().restartVirtualInstance(hwUid),
        onSuccess: () => invalidateVirtualInstances(queryClient),
    });
}
export function useStartAllVirtualInstances() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (all) => getConfiguredApiClient().startAllVirtualInstances(!!all),
        onSuccess: () => invalidateVirtualInstances(queryClient),
    });
}
export function useStopAllVirtualInstances() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => getConfiguredApiClient().stopAllVirtualInstances(),
        onSuccess: () => invalidateVirtualInstances(queryClient),
    });
}
export function useInputVirtualInstance() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ hwUid, channel, values }) => getConfiguredApiClient().inputVirtualInstance(hwUid, channel, values),
        onSuccess: () => invalidateVirtualInstances(queryClient),
    });
}
/**
 * The full unclaim dance for a hand-claimed virtual unit (plan carried
 * findings): mint an Edge presence challenge, answer it in the emulator
 * process, then unclaim with the encoded header. No CLI equivalent exists
 * because the commissioning macro attests in-process instead.
 */
export function usePresenceUnclaimVirtualInstance() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ gatewayCode, hwUid, operation = 'gateway_unclaim' }) => {
            const client = getConfiguredApiClient();
            const { challenge } = await client.requestPresenceChallenge(gatewayCode, operation);
            const { presence } = await client.virtualInstancePresenceAttestation(hwUid, challenge);
            return client.unclaimGateway(gatewayCode, encodePresenceHeader(presence));
        },
        onSuccess: () => invalidateVirtualInstances(queryClient),
    });
}
export function useVirtualInstancePresenceAttestation() {
    return useMutation({
        mutationFn: ({ hwUid, challenge }) => getConfiguredApiClient().virtualInstancePresenceAttestation(hwUid, challenge),
    });
}
export function useVirtualFleet(options) {
    return useQuery({
        queryKey: queryKeys.virtualFleet,
        queryFn: () => getConfiguredApiClient().getVirtualFleet(),
        retry: false,
        refetchInterval: 5_000,
        ...options,
    });
}
export function useVirtualFleetRoles(options) {
    return useQuery({
        queryKey: queryKeys.virtualFleetRoles,
        queryFn: () => getConfiguredApiClient().getVirtualFleetRoles(),
        retry: false,
        ...options,
    });
}
function invalidateVirtualFleet(queryClient) {
    queryClient.invalidateQueries({ queryKey: queryKeys.virtualFleet });
    invalidateVirtualInstances(queryClient);
}
export function useCommissionVirtualFleet() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (params) => getConfiguredApiClient().commissionVirtualFleet(params),
        onSuccess: () => invalidateVirtualFleet(queryClient),
    });
}
export function useDecommissionVirtualFleet() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => getConfiguredApiClient().decommissionVirtualFleet(),
        onSuccess: () => invalidateVirtualFleet(queryClient),
    });
}
export function useRecoverVirtualFleet() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (params) => getConfiguredApiClient().recoverVirtualFleet(params),
        onSuccess: () => invalidateVirtualFleet(queryClient),
    });
}
export function useDriveVirtualFleetChannel() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ unit, channel, values }) => getConfiguredApiClient().driveVirtualFleetChannel(unit, channel, values),
        onSuccess: () => invalidateVirtualFleet(queryClient),
    });
}
export function useDriveVirtualFleetFlag() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ unit, flag, value }) => getConfiguredApiClient().driveVirtualFleetFlag(unit, flag, value),
        onSuccess: () => invalidateVirtualFleet(queryClient),
    });
}
export function useMeasureVirtualFleetRate() {
    return useMutation({
        mutationFn: (durationMs) => getConfiguredApiClient().measureVirtualFleetRate(durationMs ?? undefined),
    });
}
export function useCycleVirtualFleet() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (params) => getConfiguredApiClient().cycleVirtualFleet(params),
        onSuccess: () => invalidateVirtualFleet(queryClient),
    });
}
//# sourceMappingURL=useApi.js.map